import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
  IconButton,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
import { adImageHint, validateAdImage, AD_IMAGE_ALLOWED_TYPES } from '../../constants/adImage';
import { groupDistricts, canonicalDistrict } from '../../utils/districtAliases';

const AD_TYPE = 'fullpage';
const canonicalOf = (n) => canonicalDistrict(n);

const CreateFullpageAd = () => {
  const { can } = usePermissions();
  // ==========================
  // Form State Variables
  // ==========================
  const [header, setHeader] = useState('');
  const [details, setDetails] = useState('');
  const [adFile, setAdFile] = useState(null);
  const [adBase64, setAdBase64] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [timeSlot, setTimeSlot] = useState('');

  // ==========================
  // Provider State Variables
  // ==========================
  const [providerPhone, setProviderPhone] = useState('');
  const [providerId, setProviderId] = useState('');
  const [providerName, setProviderName] = useState('');
  const [providerDialogOpen, setProviderDialogOpen] = useState(false);
  const [addProviderDialogOpen, setAddProviderDialogOpen] = useState(false);
  const [newProviderData, setNewProviderData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    whatsapp: '',
    address: '',
    pincode: '',
  });

  // ==========================
  // Location State Variables
  // ==========================
  // districts: raw District docs; districtGroups: one entry per real district (spelling variants merged)
  const [districts, setDistricts] = useState([]);
  // District mode: selected canonical district names
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  // Constituency mode: districts chosen first, then constituencies of those districts
  const [talukDistricts, setTalukDistricts] = useState([]);
  const [talukOptions, setTalukOptions] = useState([]); // [{ key, label, ids: [Taluk _id] }]
  const [selectedTaluks, setSelectedTaluks] = useState([]); // option keys
  const [selectionMode, setSelectionMode] = useState('district');
  const [imageWarning, setImageWarning] = useState('');
  const pincodeReq = useRef(0); // guards against out-of-order pincode responses
  const districtGroups = groupDistricts(districts);

  // ==========================
  // Pincode Management
  // ==========================
  const [selectedPincodes, setSelectedPincodes] = useState([]);

  // ==========================
  // Snackbar State
  // ==========================
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // ==========================
  // Fetch Districts on Component Mount
  // ==========================
  useEffect(() => {
    fetchDistricts();
  }, []);

  const fetchDistricts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/districts`);
      setDistricts(response.data);
    } catch (error) {
      console.error('Error fetching districts:', error);
      showSnackbar('Failed to fetch districts.', 'error');
    }
  };

  // Constituencies of the chosen districts (all spellings of each district), one option per
  // constituency name per district.
  useEffect(() => {
    if (selectionMode !== 'taluk') return undefined;
    const ids = districtGroups.filter((g) => talukDistricts.includes(g.name)).flatMap((g) => g.ids);
    if (ids.length === 0) {
      setTalukOptions([]);
      setSelectedTaluks([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const lists = await Promise.all(ids.map((id) => axios.get(`${API_BASE_URL}/taluks/district/${id}`)));
        if (cancelled) return;
        const map = new Map();
        lists.forEach((r) => (Array.isArray(r.data) ? r.data : []).forEach((t) => {
          const dName = t.district && t.district.name ? t.district.name : '';
          const key = `${canonicalOf(dName)}|${String(t.name || '').trim().toLowerCase()}`;
          if (!map.has(key)) map.set(key, { key, label: `${t.name} (${canonicalOf(dName)})`, ids: [] });
          if (!map.get(key).ids.includes(t._id)) map.get(key).ids.push(t._id);
        }));
        const options = [...map.values()].sort((x, y) => x.label.localeCompare(y.label));
        setTalukOptions(options);
        // drop selected constituencies that no longer belong to the chosen districts
        setSelectedTaluks((prev) => prev.filter((k) => map.has(k)));
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching constituencies:', error);
        showSnackbar('Failed to fetch constituencies.', 'error');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionMode, talukDistricts, districts]);

  // Pincodes follow the selection automatically: any change (add OR remove) recomputes them from
  // what remains selected; an empty selection simply gives an empty list (no request, no error).
  useEffect(() => {
    if (selectionMode === 'manual') return;
    let url;
    let body;
    if (selectionMode === 'district') {
      const ids = districtGroups.filter((g) => selectedDistricts.includes(g.name)).flatMap((g) => g.ids);
      url = 'alldistrictIds';
      body = { districtIds: ids };
    } else {
      const ids = talukOptions.filter((o) => selectedTaluks.includes(o.key)).flatMap((o) => o.ids);
      url = 'alltalukIds';
      body = { talukIds: ids };
    }
    const list = Object.values(body)[0];
    const reqId = ++pincodeReq.current;
    if (list.length === 0) {
      setSelectedPincodes([]);
      return;
    }
    (async () => {
      try {
        const response = await axios.post(`${API_BASE_URL}/pincodes/${url}`, body);
        if (reqId !== pincodeReq.current) return; // a newer selection superseded this response
        setSelectedPincodes(response.data.pincodes || []);
      } catch (error) {
        if (reqId !== pincodeReq.current) return;
        console.error('Error fetching pincodes:', error);
        showSnackbar('Failed to fetch pincodes.', 'error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionMode, selectedDistricts, selectedTaluks, talukOptions, districts]);

  // ==========================
  // Provider Search Handler
  // ==========================
  const handleProviderSearch = async () => {
    if (!providerPhone.trim()) {
      showSnackbar('Please enter a provider phone number.', 'warning');
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/providers/phone/${providerPhone}`);
      const provider = response.data.provider;
      if (provider) {
        setProviderId(provider._id);
        setProviderName(provider.name);
        setProviderDialogOpen(true);
      } else {
        showSnackbar('Provider not found.', 'error');
      }
    } catch (error) {
      console.error('Error searching provider:', error);
      showSnackbar('Provider not found.', 'error');
    }
  };

  // ==========================
  // Add New Provider Handler
  // ==========================
  const handleAddNewProvider = async () => {
    const { name, email, phoneNumber, whatsapp, address, pincode } = newProviderData;
    if (!name || !email || !phoneNumber || !address || !pincode) {
      showSnackbar('Please fill all required fields for the new provider.', 'warning');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/providers`, newProviderData);
      if (response.status === 201) {
        showSnackbar('Provider created successfully.', 'success');
        setAddProviderDialogOpen(false);
        setProviderId(response.data.provider._id);
        setProviderName(response.data.provider.name);
      } else {
        showSnackbar('Failed to create provider.', 'error');
      }
    } catch (error) {
      console.error('Error creating provider:', error);
      showSnackbar('Error creating provider.', 'error');
    }
  };

  // ==========================
  // Create Ad Handler
  // ==========================
  const handleCreateAd = async () => {
    if (!can('ads.fullpage.create')) return;
    if (
      !header.trim() ||
      !details.trim() ||
      !adBase64 ||
      !startDate ||
      !endDate ||
      !timeSlot ||
      !providerId ||
      selectedPincodes.length === 0
    ) {
      showSnackbar('Please fill all required fields.', 'error');
      return;
    }
    if (format(endDate, 'yyyy-MM-dd') < format(startDate, 'yyyy-MM-dd')) {
      showSnackbar('End date cannot be before the start date.', 'error');
      return;
    }

    const adData = {
      header,
      details,
      ad: adBase64,
      // plain calendar days: the server stores them as India days, whatever the browser timezone
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
      timeSlot,
      providerId,
      pincodes: selectedPincodes,
      adType: 'fullpage',
      status: 'active',
    };

    try {
      const response = await axios.post(`${API_BASE_URL}/ads`, adData);
      if (response.status === 201) {
        showSnackbar('Ad created successfully!', 'success');
        resetForm();
      } else {
        showSnackbar('Failed to create ad.', 'error');
      }
    } catch (error) {
      console.error('Error creating ad:', error);
      const msg = (error.response && error.response.status === 413)
        ? 'Ad image is too large. Please upload a smaller image (' + adImageHint(AD_TYPE) + ').'
        : (error.response && error.response.data && error.response.data.message) ? error.response.data.message : 'Failed to create ad.';
      showSnackbar(msg, 'error');
    }
  };

  // ==========================
  // Reset Form Handler
  // ==========================
  const resetForm = () => {
    setHeader('');
    setDetails('');
    setAdFile(null);
    setAdBase64('');
    setStartDate(null);
    setEndDate(null);
    setTimeSlot('');
    setProviderPhone('');
    setProviderId('');
    setProviderName('');
    setSelectedDistricts([]);
    setTalukDistricts([]);
    setTalukOptions([]);
    setSelectedTaluks([]);
    setSelectedPincodes([]);
    setSelectionMode('district');
    setImageWarning('');
  };

  // ==========================
  // Image Upload Handler
  // ==========================
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = ''; // allow re-selecting the same file after a rejection
    if (!file) return;
    setImageWarning('');
    const check = await validateAdImage(file, AD_TYPE);
    if (!check.ok) {
      setAdFile(null);
      setAdBase64('');
      showSnackbar(check.error, 'error');
      return;
    }
    if (check.warning) setImageWarning(check.warning);
    const reader = new FileReader();
    reader.onloadend = () => setAdBase64(reader.result);
    reader.readAsDataURL(file);
    setAdFile(file);
  };

  // ==========================
  // Snackbar Handlers
  // ==========================
  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  // ==========================
  // Selection Mode Handlers
  // ==========================
  const handleSelectionModeChange = (mode) => {
    pincodeReq.current += 1; // discard any in-flight pincode response from the previous mode
    setSelectionMode(mode);
    setSelectedDistricts([]);
    setTalukDistricts([]);
    setTalukOptions([]);
    setSelectedTaluks([]);
    setSelectedPincodes([]);
  };

  // ==========================
  // JSX Return
  // ==========================
  return (
     <Box sx={{ position: 'relative', padding: 2 }}>
                  {/* Place BackButton outside the Grid */}
                  <BackButton />
                  <br/>  <br/>
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Grid container spacing={2} justifyContent="center" style={{ padding: '20px' }}>
        {/* Main Card */}
        <Grid item xs={12} md={10}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Create Fullpage Ad
              </Typography>
              <Grid container spacing={3}>
                {/* Left Section - 60% Width */}
                <Grid item xs={12} md={7}>
                  <Grid container spacing={2}>
                    {/* Header */}
                    <Grid item xs={12}>
                      <TextField
                        label="Header"
                        variant="outlined"
                        fullWidth
                        required
                        value={header}
                        onChange={(e) => setHeader(e.target.value)}
                      />
                    </Grid>
                    {/* Details */}
                    <Grid item xs={12}>
                      <TextField
                        label="Details"
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={4}
                        required
                        value={details}
                        onChange={(e) => setDetails(e.target.value)}
                      />
                    </Grid>
                    {/* Ad Image Upload */}
                    <Grid item xs={12}>
                      <Button variant="contained" component="label">
                        Upload Ad Image
                        <input
                          type="file"
                          hidden
                          accept={AD_IMAGE_ALLOWED_TYPES.join(',')}
                          onChange={handleImageUpload}
                          required
                        />
                      </Button>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {adImageHint(AD_TYPE)}
                      </Typography>
                      {imageWarning && (
                        <Alert severity="warning" sx={{ mt: 1 }}>{imageWarning}</Alert>
                      )}
                      {adFile && (
                        <Typography variant="body2" style={{ marginTop: '8px' }}>
                          Selected Image: {adFile.name}
                        </Typography>
                      )}
                    </Grid>
                    {/* Start Date */}
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label="Start Date"
                        value={startDate}
                        onChange={(newValue) => setStartDate(newValue)}
                        renderInput={(params) => <TextField {...params} fullWidth required />}
                      />
                    </Grid>
                    {/* End Date */}
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label="End Date"
                        value={endDate}
                        onChange={(newValue) => setEndDate(newValue)}
                        renderInput={(params) => <TextField {...params} fullWidth required />}
                      />
                    </Grid>
                    {/* Time Slot */}
                    <Grid item xs={12}>
                      <TextField
                        label="Time Slot (seconds)"
                        variant="outlined"
                        fullWidth
                        required
                        type="number"
                        value={timeSlot}
                        onChange={(e) => setTimeSlot(e.target.value)}
                        inputProps={{ min: 1 }}
                      />
                    </Grid>
                    {/* Provider Phone Number */}
                    <Grid item xs={12} sm={8}>
                      <TextField
                        label="Provider Phone Number"
                        variant="outlined"
                        fullWidth
                        required
                        value={providerPhone}
                        onChange={(e) => setProviderPhone(e.target.value)}
                        inputProps={{ maxLength: 10 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4} style={{ display: 'flex', alignItems: 'center' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handleProviderSearch}
                      >
                        Search Provider
                      </Button>
                    </Grid>
                    {/* Provider Name Display */}
                    {providerName && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Selected Provider: {providerName}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                {/* Right Section - 40% Width */}
                <Grid item xs={12} md={5}>
                  {/* Selection Mode Card */}
                  <Card variant="outlined" style={{ marginBottom: '16px' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Pincode Selection Mode
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <InputLabel>Selection Mode</InputLabel>
                            <Select
                              value={selectionMode}
                              label="Selection Mode"
                              onChange={(e) => handleSelectionModeChange(e.target.value)}
                            >
                              <MenuItem value="district">Set by District</MenuItem>
                              <MenuItem value="taluk">Set by Constituency</MenuItem>
                              <MenuItem value="manual">Set by Pincode</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* District Selection Mode */}
                  {selectionMode === 'district' && (
                    <Card variant="outlined" style={{ marginBottom: '16px' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Select Districts
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <FormControl fullWidth>
                              <InputLabel>Districts</InputLabel>
                              <Select
                                multiple
                                value={selectedDistricts}
                                label="Districts"
                                onChange={(e) => setSelectedDistricts(e.target.value)}
                              >
                                {districtGroups.map((g) => (
                                  <MenuItem key={g.name} value={g.name}>
                                    {g.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              Pincodes of the selected districts are collected automatically.
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  )}

                  {/* Taluk Selection Mode */}
                  {selectionMode === 'taluk' && (
                    <Card variant="outlined" style={{ marginBottom: '16px' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Select Constituency
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <FormControl fullWidth>
                              <InputLabel>Districts</InputLabel>
                              <Select
                                multiple
                                value={talukDistricts}
                                label="Districts"
                                onChange={(e) => setTalukDistricts(e.target.value)}
                              >
                                {districtGroups.map((g) => (
                                  <MenuItem key={g.name} value={g.name}>
                                    {g.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12}>
                            <FormControl fullWidth disabled={talukDistricts.length === 0}>
                              <InputLabel>CONSTITUENCY</InputLabel>
                              <Select
                                multiple
                                value={selectedTaluks}
                                label="CONSTITUENCY"
                                onChange={(e) => setSelectedTaluks(e.target.value)}
                              >
                                {talukOptions.map((o) => (
                                  <MenuItem key={o.key} value={o.key}>
                                    {o.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              Choose district(s) first, then constituencies. Their pincodes are collected automatically.
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  )}

                  {/* Manual Pincode Selection */}
                  {selectionMode === 'manual' && (
                    <Card variant="outlined" style={{ marginBottom: '16px' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Enter Pincodes
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Autocomplete
                              multiple
                              freeSolo
                              options={[]}
                              value={selectedPincodes}
                              onChange={(event, newValue) => {
                                const validPincodes = newValue.filter(
                                  (pincode, index) =>
                                    /^\d{6}$/.test(pincode) && newValue.indexOf(pincode) === index
                                );
                                setSelectedPincodes(validPincodes);
                              }}
                              renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                  <Chip
                                    label={option}
                                    {...getTagProps({ index })}
                                    key={option}
                                  />
                                ))
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="outlined"
                                  label="Enter Pincodes"
                                  placeholder="Type pincode and press Enter"
                                  onKeyUp={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      const input = e.target.value.trim();
                                      const pincodes = input.match(/\d{6}/g);
                                      if (pincodes) {
                                        const newPincodes = pincodes.filter(
                                          (pincode) => !selectedPincodes.includes(pincode)
                                        );
                                        setSelectedPincodes([...selectedPincodes, ...newPincodes]);
                                        e.target.value = '';
                                      }
                                    }
                                  }}
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  )}

                  {/* Selected Pincodes Display */}
                  {selectedPincodes.length > 0 && (
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Selected Pincodes ({selectedPincodes.length})
                        </Typography>
                        <Grid container spacing={1}>
                          {selectedPincodes.map((pincode) => (
                            <Grid item key={pincode}>
                              <Chip
                                label={pincode}
                                onDelete={() => 
                                  setSelectedPincodes(selectedPincodes.filter(p => p !== pincode))
                                }
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </CardContent>
                    </Card>
                  )}
                </Grid>
              </Grid>
            </CardContent>
            <CardActions>
              {can('ads.fullpage.create') && (
                <Button variant="contained" color="primary" onClick={handleCreateAd}>
                  Submit Ad
                </Button>
              )}
            </CardActions>
          </Card>
        </Grid>

        {/* Provider Confirmation Dialog */}
        <Dialog open={providerDialogOpen} onClose={() => setProviderDialogOpen(false)}>
          <DialogTitle>Confirm Provider</DialogTitle>
          <DialogContent>
            <Typography>
              Provider Name: <strong>{providerName}</strong>
            </Typography>
            <Typography>
              Phone Number: <strong>{providerPhone}</strong>
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setProviderDialogOpen(false)} color="secondary">
              Cancel
            </Button>
            <Button onClick={() => setProviderDialogOpen(false)} color="primary">
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add New Provider Dialog */}
        <Dialog open={addProviderDialogOpen} onClose={() => setAddProviderDialogOpen(false)}>
          <DialogTitle>Add New Provider</DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Name"
                  variant="outlined"
                  fullWidth
                  required
                  value={newProviderData.name}
                  onChange={(e) =>
                    setNewProviderData({ ...newProviderData, name: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Email"
                  variant="outlined"
                  fullWidth
                  required
                  type="email"
                  value={newProviderData.email}
                  onChange={(e) =>
                    setNewProviderData({ ...newProviderData, email: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Phone Number"
                  variant="outlined"
                  fullWidth
                  required
                  value={newProviderData.phoneNumber}
                  onChange={(e) =>
                    setNewProviderData({ ...newProviderData, phoneNumber: e.target.value })
                  }
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="WhatsApp Number"
                  variant="outlined"
                  fullWidth
                  value={newProviderData.whatsapp}
                  onChange={(e) =>
                    setNewProviderData({ ...newProviderData, whatsapp: e.target.value })
                  }
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Address"
                  variant="outlined"
                  fullWidth
                  required
                  multiline
                  rows={2}
                  value={newProviderData.address}
                  onChange={(e) =>
                    setNewProviderData({ ...newProviderData, address: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Pincode"
                  variant="outlined"
                  fullWidth
                  required
                  inputProps={{ maxLength: 6 }}
                  value={newProviderData.pincode}
                  onChange={(e) => {
                    const input = e.target.value;
                    if (/^\d{0,6}$/.test(input)) {
                      setNewProviderData({ ...newProviderData, pincode: input });
                    }
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddProviderDialogOpen(false)} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleAddNewProvider} color="primary">
              Add Provider
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar Notification */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Grid>
    </LocalizationProvider>
    </Box>
  );
};

export default CreateFullpageAd;